import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderLogged from './components/header_logged';
import MaterialOrdersToolbar from './components/MaterialOrdersToolbar';
import MaterialOrdersList from './components/MaterialOrdersList';
import MaterialOrderDetail from './components/MaterialOrderDetail';
import UploadOrdersPopup from './components/UploadOrdersPopup';
import UpdateOrderStatusPopup from './components/UpdateOrderStatusPopup';
import UploadSeedsOrdersPopup from './components/UploadSeedsOrdersPopup';
import UpdateSeedsOrderPopup from './components/UpdateSeedsOrderPopup';
import SuppliersPopup from './components/SuppliersPopup';
import CreateOrderPopup from './components/CreateOrderPopup';
import ReferenceGuidePopup from './components/ReferenceGuidePopup';
import { downloadOrderExample } from './utils/orderExamples';
import api from './api/client';
import './MaterialOrdersPage.css';

// ── Tab definitions ───────────────────────────────────────────────────────────
const TABS = [
  { slug: 'master-bags',       label: 'Master Bags',       materialtypeID: 2    },
  { slug: 'small-bags',        label: 'Small Bags',        materialtypeID: 1    },
  { slug: 'seeds',             label: 'Seeds',             materialtypeID: null },
  { slug: 'fertilizer-orders', label: 'Fertilizer Orders', materialtypeID: null },
  { slug: 'mini-tools',        label: 'Mini Tools',        materialtypeID: 6    },
  { slug: 'desiccant-bags',    label: 'Desiccant Bags',    materialtypeID: 3    },
  { slug: 'ready-products',    label: 'Ready Products',    materialtypeID: null },
];

// Tabs that support brand filter
const BRAND_FILTER_TABS = new Set(['master-bags', 'small-bags']);
// Tabs that support niche filter (need niche_category_id in their product table)
const NICHE_FILTER_TABS = new Set(['master-bags']);

// ── Page ─────────────────────────────────────────────────────────────────────
function MaterialOrdersPage() {
  const { tab, orderID: urlOrderID } = useParams();
  const navigate = useNavigate();

  const activeTabDef    = TABS.find(t => t.slug === tab) || TABS[0];
  const activeTab       = activeTabDef.slug;
  const selectedOrderID = urlOrderID ? decodeURIComponent(urlOrderID) : null;

  // Sync URL if tab param is missing/invalid
  useEffect(() => {
    if (!tab || !TABS.find(t => t.slug === tab)) {
      navigate(`/material-orders/${TABS[0].slug}`, { replace: true });
    }
  }, [tab, navigate]);

  // ── Filters / search ──────────────────────────────────────────────────────
  const [search,           setSearch]           = useState('');
  const [selectedBrands,   setSelectedBrands]   = useState([]);
  const [selectedNiches,   setSelectedNiches]   = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [unpaidOnly,       setUnpaidOnly]       = useState(false);

  // Meta for filter dropdowns
  const [brands, setBrands] = useState([]);
  const [niches, setNiches] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);

  // UploadOrderDropdown popup state
  const [isCreateOrderOpen,     setIsCreateOrderOpen]     = useState(false);
  const [isUploadOpen,          setIsUploadOpen]          = useState(false);
  const [isUpdateStatusOpen,    setIsUpdateStatusOpen]    = useState(false);
  const [isSuppliersOpen,       setIsSuppliersOpen]       = useState(false);
  const [isSeedsUploadOpen,     setIsSeedsUploadOpen]     = useState(false);
  const [isSeedsUpdateOpen,     setIsSeedsUpdateOpen]     = useState(false);
  const [isReferenceGuideOpen,  setIsReferenceGuideOpen]  = useState(false);

  // Increment to force-refresh the orders list after a new order is created
  const [listRefreshKey, setListRefreshKey] = useState(0);

  // Fertilizer order creation
  const [isFertCreateOpen, setIsFertCreateOpen] = useState(false);
  const [fertNewOrderID,   setFertNewOrderID]   = useState('');
  const [fertNewYear,      setFertNewYear]       = useState(new Date().getFullYear());
  const [fertNewMonth,     setFertNewMonth]      = useState(new Date().getMonth() + 1);

  // Ready Products order creation
  const [isRPCreateOpen, setIsRPCreateOpen] = useState(false);
  const [rpNewOrderID,   setRpNewOrderID]   = useState('');
  const [rpNewYear,      setRpNewYear]      = useState(new Date().getFullYear());
  const [rpNewMonth,     setRpNewMonth]     = useState(new Date().getMonth() + 1);

  // Suppliers for upload popup (per selected type in popup)
  const [uploadSuppliers,       setUploadSuppliers]       = useState([]);
  // Suppliers for seeds upload popup
  const [seedsSuppliers,        setSeedsSuppliers]        = useState([]);

  // Auto-add to stock setting
  const [autoAddStock,    setAutoAddStock]    = useState(true);

  // ── Fetch meta once ───────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.get('/plantoproduce/brands').then(r => r.data).catch(() => []),
      api.get('/plantoproduce/niches').then(r => r.data).catch(() => []),
      api.get('/api/orders_statuses').then(r => r.data).catch(() => []),
      api.get('/api/material_types').then(r => r.data).catch(() => []),
      api.get('/api/seeds_suppliers').then(r => r.data).catch(() => []),
      api.get('/api/settings/auto_add_stock').then(r => r.data).catch(() => ({ enabled: true })),
    ]).then(([b, n, s, mt, ss, setting]) => {
      setBrands(b);
      setNiches(n);
      setStatuses(s);
      setMaterialTypes(mt);
      setSeedsSuppliers(ss);
      setAutoAddStock(setting.enabled);
    });
  }, []);

  const handleAutoAddStockToggle = async () => {
    const newValue = !autoAddStock;
    setAutoAddStock(newValue); // оптимистичное обновление
    try {
      await api.put('/api/settings/auto_add_stock', { enabled: newValue });
    } catch { setAutoAddStock(!newValue); } // откат при ошибке (HTTP или сетевой)
  };

  // Reset filters on tab change (but not when drilling into an order)
  useEffect(() => {
    setSearch('');
    setSelectedBrands([]);
    setSelectedNiches([]);
    setSelectedStatuses([]);
    setUnpaidOnly(false);
  }, [activeTab]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleTabClick = (slug) => navigate(`/material-orders/${slug}`);

  const handleSelectOrder = (orderID) =>
    navigate(`/material-orders/${activeTab}/${encodeURIComponent(orderID)}`);

  const handleBack = () => navigate(`/material-orders/${activeTab}`);

  const handleUploadMaterialTypeChange = async (typeID) => {
    if (!typeID) { setUploadSuppliers([]); return; }
    try {
      const res = await api.get(`/api/materialsuppliers_for_type/${typeID}`);
      setUploadSuppliers(res.data);
    } catch { setUploadSuppliers([]); }
  };

  const handleDownloadExample = () => downloadOrderExample('materials');

  const MONTH_NAMES_FULL = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  const handleFertCreateSubmit = () => {
    const oid = fertNewOrderID.trim();
    const key = oid || `period-${fertNewYear}-${String(fertNewMonth).padStart(2,'0')}`;
    setIsFertCreateOpen(false);
    navigate(`/material-orders/fertilizer-orders/${encodeURIComponent(key)}`);
  };

  const handleRPCreateSubmit = () => {
    const oid = rpNewOrderID.trim();
    const key = oid || `period-${rpNewYear}-${String(rpNewMonth).padStart(2,'0')}`;
    setIsRPCreateOpen(false);
    navigate(`/material-orders/ready-products/${encodeURIComponent(key)}`);
  };

  const handleDownloadSeedsExample = () => downloadOrderExample('seeds');

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="mo-root">
      <HeaderLogged />
      <div className="mo-container">

        {/* Page header */}
        <div className="mo-page-header">
          <div>
            <h2 className="mo-title">Material Orders</h2>
            <p className="mo-subtitle">Track and manage purchase orders for all material types</p>
          </div>
          <button
            className={`mo-toggle-btn${autoAddStock ? ' mo-toggle-btn--on' : ' mo-toggle-btn--off'}`}
            onClick={handleAutoAddStockToggle}
            title={autoAddStock ? 'Auto-add to stock on delivery: ON' : 'Auto-add to stock on delivery: OFF'}
          >
            <span className="mo-toggle-track">
              <span className="mo-toggle-thumb" />
            </span>
            <span className="mo-toggle-label">Auto-add to stock on delivery</span>
          </button>
        </div>

        {/* Toolbar — только в списке: в открытом заказе поиск и фильтры списка не нужны */}
        {!selectedOrderID && (
        <MaterialOrdersToolbar
          search={search}
          onSearchChange={setSearch}
          brands={brands}
          niches={niches}
          statuses={statuses}
          selectedBrands={selectedBrands}
          setSelectedBrands={setSelectedBrands}
          selectedNiches={selectedNiches}
          setSelectedNiches={setSelectedNiches}
          selectedStatuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          unpaidOnly={unpaidOnly}
          setUnpaidOnly={setUnpaidOnly}
          showBrandFilter={BRAND_FILTER_TABS.has(activeTab)}
          showNicheFilter={NICHE_FILTER_TABS.has(activeTab)}
          onCreateOrder={() => setIsCreateOrderOpen(true)}
          onAddOrder={() => setIsUploadOpen(true)}
          onDownloadExample={handleDownloadExample}
          onReferenceGuide={() => setIsReferenceGuideOpen(true)}
          onUpdateOrderStatus={() => setIsUpdateStatusOpen(true)}
          onManageSuppliers={() => setIsSuppliersOpen(true)}
          onAddSeedsOrder={() => setIsSeedsUploadOpen(true)}
          onDownloadSeedsExample={handleDownloadSeedsExample}
          onUpdateSeedsOrder={() => setIsSeedsUpdateOpen(true)}
        />
        )}

        {/* Tabs */}
        {!selectedOrderID && (
          <div className="mo-tabs">
            {TABS.map(t => (
              <button
                key={t.slug}
                className={`mo-tab${activeTab === t.slug ? ' active' : ''}`}
                onClick={() => handleTabClick(t.slug)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Fertilizer orders — create button */}
        {!selectedOrderID && activeTab === 'fertilizer-orders' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button
              className="mo-add-item-btn"
              onClick={() => setIsFertCreateOpen(true)}
            >
              <i className="fas fa-plus"></i> New order
            </button>
          </div>
        )}

        {/* Ready Products — create button */}
        {!selectedOrderID && activeTab === 'ready-products' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button
              className="mo-add-item-btn"
              onClick={() => setIsRPCreateOpen(true)}
            >
              <i className="fas fa-plus"></i> New order
            </button>
          </div>
        )}

        {/* Content */}
        {selectedOrderID ? (
          <MaterialOrderDetail
            orderID={selectedOrderID}
            tabKey={activeTab}
            materialtypeID={activeTabDef.materialtypeID}
            statuses={statuses}
            brands={brands}
            onBack={handleBack}
          />
        ) : (
          <MaterialOrdersList
            key={listRefreshKey}
            tabKey={activeTab}
            typeLabel={activeTabDef.label}
            materialtypeID={activeTabDef.materialtypeID}
            search={search}
            selectedBrands={selectedBrands}
            selectedNiches={selectedNiches}
            selectedStatuses={selectedStatuses}
            unpaidOnly={unpaidOnly}
            onSelectOrder={handleSelectOrder}
          />
        )}

      </div>

      {/* Create new order popup */}
      {isCreateOrderOpen && (
        <CreateOrderPopup
          materialTypes={materialTypes}
          statuses={statuses}
          seedsSuppliers={seedsSuppliers}
          onClose={() => setIsCreateOrderOpen(false)}
          onSuccess={() => { setIsCreateOrderOpen(false); setListRefreshKey(k => k + 1); }}
        />
      )}

      {/* Upload orders popup */}
      {isUploadOpen && (
        <UploadOrdersPopup
          isOpen={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          materialTypes={materialTypes}
          statuses={statuses}
          suppliers={uploadSuppliers}
          onMaterialTypeChange={handleUploadMaterialTypeChange}
          onSuccess={() => setListRefreshKey(k => k + 1)}
        />
      )}

      {/* Reference guide: file columns, statuses, suppliers */}
      {isReferenceGuideOpen && (
        <ReferenceGuidePopup
          materialTypes={materialTypes}
          statuses={statuses}
          onClose={() => setIsReferenceGuideOpen(false)}
        />
      )}

      {/* Update order status popup */}
      {isUpdateStatusOpen && (
        <UpdateOrderStatusPopup
          isOpen={isUpdateStatusOpen}
          onClose={() => setIsUpdateStatusOpen(false)}
          statuses={statuses}
          materialTypes={materialTypes}
        />
      )}

      {/* Suppliers management popup */}
      {isSuppliersOpen && (
        <SuppliersPopup
          onClose={() => setIsSuppliersOpen(false)}
          materialTypes={materialTypes}
        />
      )}

      {/* Upload seeds orders popup */}
      {isSeedsUploadOpen && (
        <UploadSeedsOrdersPopup
          suppliers={seedsSuppliers}
          onClose={() => setIsSeedsUploadOpen(false)}
          onSuccess={() => { setIsSeedsUploadOpen(false); setListRefreshKey(k => k + 1); }}
        />
      )}

      {/* Update seeds order popup */}
      {isSeedsUpdateOpen && (
        <UpdateSeedsOrderPopup
          statuses={statuses}
          onClose={() => setIsSeedsUpdateOpen(false)}
          onSuccess={() => {}}
        />
      )}

      {/* Ready Products order create popup */}
      {isRPCreateOpen && (
        <div className="mo-add-overlay" onClick={() => setIsRPCreateOpen(false)}>
          <div className="mo-add-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="mo-add-header">
              <div>
                <div className="mo-add-title">New Ready Products order</div>
                <div className="mo-add-subtitle">Set PO# and period, then add items inside the order</div>
              </div>
              <button className="mo-add-close" onClick={() => setIsRPCreateOpen(false)}>×</button>
            </div>
            <div className="mo-add-body">
              <div className="mo-add-field">
                <label className="mo-add-label">PO # (optional)</label>
                <input
                  className="mo-add-input"
                  type="text"
                  placeholder="e.g. PO-2025-001 (leave blank for period order)"
                  value={rpNewOrderID}
                  onChange={e => setRpNewOrderID(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="mo-add-row">
                <div className="mo-add-field">
                  <label className="mo-add-label">Year</label>
                  <input
                    className="mo-add-input"
                    type="number"
                    min="2020" max="2099"
                    value={rpNewYear}
                    onChange={e => setRpNewYear(parseInt(e.target.value))}
                  />
                </div>
                <div className="mo-add-field">
                  <label className="mo-add-label">Month</label>
                  <select
                    className="mo-add-input"
                    value={rpNewMonth}
                    onChange={e => setRpNewMonth(parseInt(e.target.value))}
                  >
                    {MONTH_NAMES_FULL.map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="mo-add-footer">
              <button className="mo-add-btn-cancel" onClick={() => setIsRPCreateOpen(false)}>Cancel</button>
              <button className="mo-add-btn-save" onClick={handleRPCreateSubmit}>
                Open order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fertilizer order create popup */}
      {isFertCreateOpen && (
        <div className="mo-add-overlay" onClick={() => setIsFertCreateOpen(false)}>
          <div className="mo-add-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="mo-add-header">
              <div>
                <div className="mo-add-title">New fertilizer order</div>
                <div className="mo-add-subtitle">Set PO# and period, then add items inside the order</div>
              </div>
              <button className="mo-add-close" onClick={() => setIsFertCreateOpen(false)}>×</button>
            </div>
            <div className="mo-add-body">
              <div className="mo-add-field">
                <label className="mo-add-label">PO # (optional)</label>
                <input
                  className="mo-add-input"
                  type="text"
                  placeholder="e.g. PO-2025-001 (leave blank for period order)"
                  value={fertNewOrderID}
                  onChange={e => setFertNewOrderID(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="mo-add-row">
                <div className="mo-add-field">
                  <label className="mo-add-label">Year</label>
                  <input
                    className="mo-add-input"
                    type="number"
                    min="2020" max="2099"
                    value={fertNewYear}
                    onChange={e => setFertNewYear(parseInt(e.target.value))}
                  />
                </div>
                <div className="mo-add-field">
                  <label className="mo-add-label">Month</label>
                  <select
                    className="mo-add-input"
                    value={fertNewMonth}
                    onChange={e => setFertNewMonth(parseInt(e.target.value))}
                  >
                    {MONTH_NAMES_FULL.map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="mo-add-footer">
              <button className="mo-add-btn-cancel" onClick={() => setIsFertCreateOpen(false)}>Cancel</button>
              <button className="mo-add-btn-save" onClick={handleFertCreateSubmit}>
                Open order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaterialOrdersPage;
